import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User } from '@/types/entities'

/**
 * Who a manager is currently acting as, and the credential they set aside to do it.
 *
 * Persisted with the rest of the session: a masquerade that evaporated on page reload would leave
 * a manager holding an artist token with no way back to their own account except signing in again.
 * `actorToken` is what makes ending one a local operation rather than a re-authentication.
 */
interface MasqueradeState {
  artistId: number
  artisticName: string
  actorToken: string
  actorUser: User
}

interface AuthState {
  user: User | null
  authToken: string | null
  isAuthenticated: boolean
  isHydrated: boolean
  masquerade: MasqueradeState | null
  beginMasquerade: (m: MasqueradeState, artistUser: User, artistToken: string) => void
  endMasquerade: () => void
  setUser: (user: User | null) => void
  setUserSync: (user: User | null) => Promise<void>
  setToken: (token: string | null) => void
  clearUser: () => void
  setHydrated: (hydrated: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      authToken: null,
      isAuthenticated: false,
      isHydrated: false,
      masquerade: null,
      beginMasquerade: (m, artistUser, artistToken) => {
        set({ masquerade: m, user: artistUser, authToken: artistToken, isAuthenticated: true })
      },
      endMasquerade: () => {
        const current = useAuthStore.getState().masquerade
        if (!current) return
        // Straight back to the actor's own stored credential — no round trip, so ending a
        // masquerade cannot fail because the network did.
        set({
          masquerade: null,
          user: current.actorUser,
          authToken: current.actorToken,
          isAuthenticated: true,
        })
      },
      setUser: (user) => {
        set({ user, isAuthenticated: !!user })
      },
      setUserSync: async (user) => {
        set({ user, isAuthenticated: !!user })
        
        // Wait for persist to complete by checking localStorage
        await new Promise<void>((resolve) => {
          // Give the persist middleware time to write
          const checkInterval = setInterval(() => {
            const stored = localStorage.getItem('auth-storage')
            if (stored) {
              try {
                const parsed = JSON.parse(stored)
                if (parsed.state?.user?.email === user?.email) {
                  clearInterval(checkInterval)
                  resolve()
                }
              } catch (e) {
                // Continue checking
              }
            } else if (!user) {
              // If clearing user, wait for it to be removed
              clearInterval(checkInterval)
              resolve()
            }
          }, 10)
          
          // Timeout after 1 second
          setTimeout(() => {
            clearInterval(checkInterval)
            resolve()
          }, 1000)
        })
      },
      setToken: (token) => {
        set({ authToken: token })
      },
      clearUser: () => {
        set({ user: null, authToken: null, isAuthenticated: false, masquerade: null })
      },
      setHydrated: (hydrated) => set({ isHydrated: hydrated }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        // Mark as hydrated after rehydration completes
        state?.setHydrated(true)
      },
    }
  )
)

